import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, LOGO_MOUNTAIN } from "../lib/api";
import ProductCard from "../components/ProductCard";
import SEO, { orgSchema, localBusinessSchema, websiteSchema } from "../components/SEO";
import { Sparkles, Truck, ShieldCheck, HandHeart, MapPin, Star, Instagram } from "lucide-react";

export default function Home() {
  const [products, setProducts] = useState([]);
  const [promo, setPromo] = useState({ percent: 10, active: true });
  useEffect(() => {
    http.get("/products").then((r) => setProducts(r.data));
    http.get("/promo/current").then((r) => setPromo(r.data));
  }, []);
  const bestsellers = products.filter((p) => p.featured && p.status === "active").slice(0, 4);
  const honey = products.filter((p) => p.category === "honey" && p.status === "active").slice(0, 4);
  const supplements = products.filter((p) => p.category === "supplements" && p.status === "active").slice(0, 4);
  const bySlug = Object.fromEntries(products.map((p) => [p.slug, p]));
  const heroImages = ["yemen-sidr-honey","ashwagandha","lions-mane","himalayan-shilajit"].map((s) => bySlug[s]).filter(Boolean);

  return (
    <div>
      <SEO
        title="Organic Healing UK | Premium Honey & Natural Supplements"
        description="Shop premium Sidr honey, natural botanicals and food supplements from Organic Healing UK. Secure checkout, UK delivery and collection available."
        path="/"
        jsonLd={[orgSchema, localBusinessSchema, websiteSchema]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-teal-500/10 via-lime-500/5 to-transparent blur-3xl"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 pt-24 pb-20 text-center space-y-8">
          <span className="oh-badge oh-badge-lime" data-testid="hero-eyebrow">The Natural Path · Birmingham, UK</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl mx-auto">
            Premium <span className="text-amber-300">Honey</span> & Natural <span className="text-lime-400">Wellness</span> Products
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            Discover carefully selected honey, botanicals and food supplements from Organic Healing. Shop securely online for UK delivery or convenient collection.
          </p>
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Link to="/shop" data-testid="hero-shop-all" className="oh-btn-lime px-8 py-3.5 rounded-full text-sm tracking-widest uppercase">Shop All Products</Link>
            <Link to="/honey" data-testid="hero-honey" className="oh-btn-ghost px-8 py-3.5 rounded-full text-sm tracking-widest uppercase">Explore Premium Honey</Link>
          </div>
          <span className="oh-badge oh-badge-gold inline-block">10% OFF SELECTED PRODUCTS</span>
          <div className="flex flex-wrap gap-8 justify-center pt-2 text-xs font-mono text-white/60 tracking-widest">
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-lime-400"/> SECURE CHECKOUT</span>
            <span className="flex items-center gap-2"><Truck size={14} className="text-amber-400"/> UK DELIVERY</span>
            <span className="flex items-center gap-2"><MapPin size={14} className="text-teal-400"/> COLLECTION AVAILABLE</span>
          </div>
        </div>
      </section>

      {/* COLLECTION IN BIRMINGHAM CALLOUT */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 pb-4">
        <div className="oh-card-honey rounded-2xl p-6 lg:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 border border-amber-500/30" data-testid="collect-strip">
          <MapPin size={22} className="text-amber-300 shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="font-serif text-lg sm:text-xl leading-tight">
              Order online or collect in-store · <span className="text-amber-300">Birmingham Small Heath</span>
            </div>
            <div className="text-[11px] sm:text-xs text-white/60 font-mono tracking-widest mt-1 break-words">
              CARD & CASH ACCEPTED · UNIT 13D, 47 WHITMORE RD · B10 0NR
            </div>
          </div>
          <Link
            to="/delivery"
            data-testid="collect-cta"
            className="oh-btn-gold px-5 py-2.5 rounded-full text-xs tracking-widest uppercase whitespace-nowrap self-start md:self-auto shrink-0">
            Collection Info →
          </Link>
        </div>
      </section>

      {/* CERTIFICATIONS STRIP */}
      <section className="border-y border-[var(--oh-border)] bg-[var(--oh-surface)]/60">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { title: "Food Hygiene", sub: "Registered · UK" },
            { title: "Food Standards", sub: "Agency Compliant" },
            { title: "Natural Sourcing", sub: "Traceable Origin" },
            { title: "Organic Focus", sub: "Where Available" },
          ].map((c, i) => (
            <div key={i} className="flex flex-col items-center gap-1" data-testid={`cert-${i}`}>
              <div className="text-lime-400 font-mono text-xs tracking-[0.25em] uppercase">{c.title}</div>
              <div className="text-[11px] text-white/50 font-mono">{c.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BESTSELLERS – removed to avoid repetition with Honey & Supplement sections below */}

      {/* PREMIUM HONEY */}
      <Section title="Premium Honey Collection" eyebrow="Raw & Traceable" cta={{ to: "/honey", label: "See All Honey" }}>
        <Grid>{honey.map((p) => <ProductCard key={p.id} product={p} promoPercent={promo.active ? promo.percent : 0}/>) }</Grid>
      </Section>

      {/* SUPPLEMENTS */}
      <Section title="Natural Supplements" eyebrow="Everyday Wellness" cta={{ to: "/supplements", label: "See All Supplements" }}>
        <Grid>{supplements.map((p) => <ProductCard key={p.id} product={p} promoPercent={promo.active ? promo.percent : 0}/>) }</Grid>
      </Section>

      {/* COMING SOON TEASER (link only, full details on /coming-soon) */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="oh-card p-8 lg:p-10 rounded-3xl grid md:grid-cols-[1fr,auto] gap-6 items-center border-lime-500/30">
          <div>
            <span className="oh-badge oh-badge-lime">Launching Next</span>
            <h2 className="font-serif text-3xl mt-3 mb-2">Two new supplements coming soon</h2>
            <p className="text-white/70 text-sm max-w-xl">Magnesium Glycinate 3-in-1 and Turmeric with Curcumin, Ginger & Black Pepper are launching shortly. Register your interest for an exclusive early-access offer.</p>
          </div>
          <Link to="/coming-soon" data-testid="home-cs-cta" className="oh-btn-lime px-6 py-3 rounded-full text-xs tracking-widest uppercase whitespace-nowrap">See Both Products →</Link>
        </div>
      </section>

      {/* CONSULTATION BOOKING */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
        <div className="oh-card-honey p-8 lg:p-12 rounded-3xl relative overflow-hidden" data-testid="cons-teaser">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-gradient-to-br from-amber-500/25 via-lime-500/10 to-transparent blur-3xl"></div>
          <div className="relative grid lg:grid-cols-[1.4fr,auto] gap-8 items-center">
            <div>
              <span className="oh-badge oh-badge-gold">Treatment with Qur'an & Sunnah</span>
              <h2 className="font-serif text-3xl sm:text-4xl mt-3 mb-3">Book a Consultation</h2>
              <p className="text-white/75 max-w-2xl leading-relaxed">
                A private consultation rooted in the <b>Qur'an and Sunnah</b> for those facing the difficulties of daily life. £50 fee covers a 30 – 60 minute session. Any additional charges will be discussed directly with the mentor.
              </p>
              <div className="flex flex-wrap gap-2 mt-5">
                {["Health","Family & Marriage","Rizq & Work","Anxiety & Unrest","Sleep","General Wellbeing"].map((t) => (
                  <span key={t} className="oh-badge oh-badge-turq">{t}</span>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Link to="/consultation" className="oh-btn-gold px-6 py-3 rounded-full text-xs tracking-widest uppercase whitespace-nowrap text-center" data-testid="cons-cta">Book a Consultation →</Link>
              <a href="https://wa.me/447877855305" target="_blank" rel="noreferrer" className="oh-btn-ghost px-6 py-3 rounded-full text-xs tracking-widest uppercase whitespace-nowrap text-center">Ask on WhatsApp</a>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <div className="text-center mb-14">
          <span className="oh-badge oh-badge-turq">Our Standards</span>
          <h2 className="font-serif text-4xl mt-4">Why Choose Organic Healing</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: <Sparkles/>, title: "Carefully Selected", body: "Every product is hand-picked for provenance and quality." },
            { icon: <ShieldCheck/>, title: "Traceable Sourcing", body: "Batch numbers, country of origin and expiry — always visible." },
            { icon: <HandHeart/>, title: "Clear Ingredients", body: "Honest labels. No hidden extras. No inflated health claims." },
            { icon: <Truck/>, title: "UK Delivery & Collection", body: "Free UK delivery over £40. Local collection available." },
            { icon: <ShieldCheck/>, title: "Secure Payments", body: "Stripe-powered checkout with Apple Pay & Google Pay." },
            { icon: <HandHeart/>, title: "Friendly Support", body: "Real people, real answers. Get in touch anytime." },
          ].map((f, i) => (
            <div key={i} className="oh-card p-6 space-y-3" data-testid={`why-${i}`}>
              <div className="text-lime-400">{f.icon}</div>
              <h3 className="font-serif text-2xl">{f.title}</h3>
              <p className="text-sm text-white/65">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SOCIAL - removed (see footer + contact page) */}

      {/* MAP + REVIEWS + NEWSLETTER */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14 grid lg:grid-cols-2 gap-10">
        <div className="oh-card p-8">
          <span className="oh-badge oh-badge-turq"><MapPin size={12} className="inline"/> Store & Collection</span>
          <h2 className="font-serif text-3xl mt-4 mb-4">Visit Us in Birmingham</h2>
          <address className="not-italic text-white/85 leading-relaxed mb-4">
            Unit 13D, 47 Whitmore Rd<br/>
            Small Heath, Birmingham<br/>
            <span className="font-mono">B10 0NR</span>
          </address>
          <div className="space-y-1 text-sm text-white/70 mb-5">
            <div>Monday – Saturday · 10:00 – 18:00</div>
            <div className="text-white/50">Sunday closed</div>
          </div>
          <div className="space-y-1 text-sm mb-5">
            <div className="font-mono text-white/85">+44 7877 855305 <span className="text-white/50">· WhatsApp welcome</span></div>
            <div className="font-mono text-white/85">organichealing2024@gmail.com</div>
          </div>
          <a href="https://share.google/IdgRxjhYFkvqQnqoK" target="_blank" rel="noreferrer" data-testid="map-directions" className="oh-btn-lime px-5 py-2.5 rounded-full text-xs tracking-widest uppercase inline-block">Open in Google Maps →</a>
          <div className="mt-5 text-xs text-white/60 border-t border-[var(--oh-border)] pt-4">
            <b className="text-amber-300">In-store collection available.</b> Order online then collect in Birmingham — card & cash accepted.
          </div>
        </div>
        <div className="oh-card p-8">
          <span className="oh-badge oh-badge-gold"><Star size={12} className="inline"/> Customer Reviews</span>
          <h2 className="font-serif text-3xl mt-4 mb-3">What Customers Say</h2>
          <p className="text-white/70 text-sm mb-4">Every review below is a verified customer purchase. We never publish fabricated feedback.</p>
          <div className="flex gap-3 flex-wrap">
            <Link to="/reviews" data-testid="see-reviews" className="oh-btn-lime px-5 py-2.5 rounded-full text-xs tracking-widest uppercase">Read Reviews</Link>
            <a href="https://share.google/IdgRxjhYFkvqQnqoK" target="_blank" rel="noreferrer" className="oh-btn-ghost px-5 py-2.5 rounded-full text-xs tracking-widest uppercase">Leave a Review</a>
          </div>
        </div>
      </section>

      <NewsletterCTA/>
    </div>
  );
}

function Section({ title, eyebrow, cta, children, ...rest }) {
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14" {...rest}>
      <div className="flex items-end justify-between mb-8 gap-6 flex-wrap">
        <div>
          <span className="oh-badge oh-badge-turq">{eyebrow}</span>
          <h2 className="font-serif text-4xl mt-3">{title}</h2>
        </div>
        {cta && <Link to={cta.to} className="oh-btn-ghost px-4 py-2 rounded-full text-xs tracking-widest uppercase">{cta.label}</Link>}
      </div>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{children}</div>; }

export function NewsletterCTA() {
  const [email, setEmail] = useState(""); const [ok, setOk] = useState(false);
  return (
    <section className="max-w-7xl mx-auto px-4 lg:px-8 py-14">
      <div className="oh-card p-10 rounded-3xl bg-gradient-to-br from-[#0F2E36] to-[#0B2228] text-center space-y-5">
        <span className="oh-badge oh-badge-lime">Stay Rooted</span>
        <h2 className="font-serif text-4xl">Get 10% off your first order</h2>
        <p className="text-white/70 max-w-md mx-auto text-sm">Join our newsletter for harvest updates, new arrivals and gentle wellness ideas — no spam.</p>
        <form onSubmit={async (e) => { e.preventDefault(); await http.post("/newsletter",{email}); setOk(true); }} className="flex gap-3 max-w-md mx-auto">
          <input required type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="your@email.com" className="oh-input" data-testid="newsletter-email"/>
          <button className="oh-btn-lime px-6 py-2 rounded-full text-xs tracking-widest uppercase" data-testid="newsletter-submit">Subscribe</button>
        </form>
        {ok && <div className="text-lime-400 text-sm">✓ Thank you — please check your inbox.</div>}
      </div>
    </section>
  );
}
